import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';

@Injectable({
	providedIn: 'root',
})
export class MonacoEditorService {
	loaded = false;

	public loadingFinished: Subject<void> = new Subject<void>();

	constructor() { }

	private finishLoading() {
		this.loaded = true;
		this.loadingFinished.next();
		this.registerSrt();

		// $$api typings for monaco-editor, would be nice to use bb-player-api.ts in the future
		(window as any).monaco?.languages?.typescript?.javascriptDefaults?.addExtraLib(`declare const $$api: {
			appendSanitizedHtmlTo: (...args: any[]) => void;
			autoPlayNextCancel: (...args: any[]) => void;
			decreaseSizerRate: (...args: any[]) => void;
			disableInterfaceElement: (...args: any[]) => void;
			doOpenUrl: (...args: any[]) => void;
			doShare: (...args: any[]) => void;
			enableInterfaceElement: (...args: any[]) => void;
			exit: (...args: any[]) => void;
			fullscreen: (...args: any[]) => void;
			getAdLogs: (...args: any[]) => any;
			getAdPodInfo: (...args: any[]) => any;
			getAssets: (...args: any[]) => any;
			getAudioTracks: (...args: any[]) => any;
			getBuffered: (...args: any[]) => any;
			getCastSource: (...args: any[]) => any;
			getChapterList: (...args: any[]) => any;
			getClipData: (...args: any[]) => any;
			getContentLayer: (...args: any[]) => any;
			getCurrentAd: (...args: any[]) => any;
			getCurrentAsset: (...args: any[]) => any;
			getCurrentAudioTrack: (...args: any[]) => any;
			getCurrentHeadType: (...args: any[]) => any;
			getCurrentSubtitle: (...args: any[]) => any;
			getCurrentSubtitleLine: (...args: any[]) => any;
			getCurrentTime: (...args: any[]) => any;
			getDeeplink: (...args: any[]) => any;
			getDetections: (...args: any[]) => any;
			getDuration: (...args: any[]) => any;
			getDvid: (...args: any[]) => any;
			getEmbedcode: (...args: any[]) => any;
			getFlag: (...args: any[]) => any;
			getLocalStorageItem: (...args: any[]) => any;
			getLogo: (...args: any[]) => any;
			getMode: (...args: any[]) => any;
			getMuted: (...args: any[]) => any;
			getOpenUrlParameters: (...args: any[]) => any;
			getPhase: (...args: any[]) => any;
			getPlaybackRate: (...args: any[]) => any;
			getPlayerPath: (...args: any[]) => any;
			getPlayoutData: (...args: any[]) => any;
			getPreviewAsset: (...args: any[]) => any;
			getProgramMuted: (...args: any[]) => any;
			getProgramVolume: (...args: any[]) => any;
			getProjectData: (...args: any[]) => any;
			getPropertyValue: (...args: any[]) => any;
			getRelatedClips: (...args: any[]) => any;
			getRelatedItems: (...args: any[]) => any;
			getRootUrl: (...args: any[]) => any;
			getSharing: (...args: any[]) => any;
			getSkinContainer: (...args: any[]) => any;
			getSkinLayer: (...args: any[]) => any;
			getState: (...args: any[]) => any;
			getSubtitleLayer: (...args: any[]) => any;
			getSubtitlesList: (...args: any[]) => any;
			getThumbnail: (...args: any[]) => any;
			getViewPageUrl: (...args: any[]) => any;
			getVolume: (...args: any[]) => any;
			getWrapper: (...args: any[]) => any;
			hasSharingOption: (...args: any[]) => any;
			hideInterfaceElement: (...args: any[]) => void;
			increaseSizerRate: (...args: any[]) => void;
			isFullscreen: (...args: any[]) => boolean;
			isPlaying: (...args: any[]) => boolean;
			isReady: (...args: any[]) => boolean;
			load: (...args: any[]) => void;
			nativeFullscreen: (...args: any[]) => any;
			off: (...args: any[]) => void;
			on: (...args: any[]) => void;
			pause: (...args: any[]) => void;
			play: (...args: any[]) => void;
			preloadAds: (...args: any[]) => void;
			reset: (...args: any[]) => void;
			retractFullscreen: (...args: any[]) => void;
			sanitizeHtml: (...args: any[]) => any;
			seek: (...args: any[]) => void;
			selectAudioTrack: (...args: any[]) => void;
			setAsset: (...args: any[]) => void;
			setDvid: (...args: any[]) => void;
			setFitMode: (...args: any[]) => void;
			setFlag: (...args: any[]) => void;
			setInView: (...args: any[]) => void;
			setLocalStorageItem: (...args: any[]) => void;
			setMuted: (...args: any[]) => void;
			setPlaybackRate: (...args: any[]) => void;
			setProgramMuted: (...args: any[]) => void;
			setProgramVolume: (...args: any[]) => void;
			setRelatedClips: (...args: any[]) => void;
			setSanitizedHtmlTo: (...args: any[]) => void;
			setSubtitle: (...args: any[]) => void;
			setVolume: (...args: any[]) => void;
			showCastTargetPicker: (...args: any[]) => void;
			showInterfaceElement: (...args: any[]) => void;
			toggleFullscreen: (...args: any[]) => void;
			triggerMessage: (...args: any[]) => void;
			updateLineItemPlayout: (...args: any[]) => void;
			updatePlayout: (...args: any[]) => void;
			destruct: (...args: any[]) => void;
		};`);
	}

	public load() {
		// load the assets
		const w = window as any;

		const baseUrl = '/resources/assets/monaco-editor/min/vs';

		if (typeof w.monaco === 'object') {
			this.finishLoading();
			return;
		}

		const onGotAmdLoader: any = () => {
			// load Monaco
			w.require.config({ paths: { vs: `${baseUrl}` } });
			w.require([`vs/editor/editor.main`], () => {
				this.finishLoading();
			});
		};

		// load AMD loader, if necessary
		if (!w.require) {
			const loaderScript: HTMLScriptElement = document.createElement('script');
			loaderScript.type = 'text/javascript';
			loaderScript.src = `${baseUrl}/loader.js`;
			loaderScript.addEventListener('load', onGotAmdLoader);
			document.body.appendChild(loaderScript);
		} else {
			onGotAmdLoader();
		}
	}

	private registerSrt(): void {
		const monaco = (window as any).monaco;

		if (!monaco?.languages || !monaco?.editor) {
			return;
		}

		if (monaco.languages.getEncodedLanguageId('srt') > 0) {
			return; // already loaded
		}

		monaco.languages.register({ id: 'srt', extionsions: ['.srt'], mimetypes: ['text/srt'] });
		monaco.languages.setMonarchTokensProvider('srt', {
			tokenizer: {
				root: [
					[/(\d{2}:\d{2}:\d{2},\d{3}($|\s)?)/gm, 'timestamp'],
					[/-->/gm, 'arrow'],
					[/^[1-9]\d*$/gm, 'counter'] // ignore starting with 0
				],
			}
		});

		monaco.languages.registerCompletionItemProvider('srt', {
			// eslint-disable-next-line arrow-body-style
			provideCompletionItems: () => {
				// do not use sugesstions (already used words in the srt) when typing. Empty object will not disable
				return {
					suggestions: [{
						label: '-',
						kind: monaco.languages.CompletionItemKind.Text,
						insertText: '-'
					},]
				};
			}
		});

		monaco.editor.defineTheme('srt', {
			base: 'vs',
			inherit: true,
			rules: [
				{ token: 'timestamp', foreground: '050585' },
				{ token: 'arrow', foreground: 'd62df3', fontStyle: 'bold' },
				{ token: 'counter', foreground: '009161', fontStyle: 'bold' }
			],
			colors: {}
		});
		monaco.editor.defineTheme('srt-dark', {
			base: 'vs-dark',
			inherit: true,
			rules: [
				{ token: 'timestamp', foreground: '88dbfe' },
				{ token: 'arrow', foreground: 'ae6ea4', fontStyle: 'bold' },
				{ token: 'counter', foreground: 'accca2', fontStyle: 'bold' }
			],
			colors: {}
		});
	}
}
